import { schemas } from '@repo/contracts'
import { z } from 'zod'
import { parseDataOfType } from '@/common/parseDataType'
import { apiErrorHandler } from '../errorHandler'

export const AdminOrgSchema = schemas.apihttp_FEOrg.describe('AdminOrg')
export type AdminOrg = z.infer<typeof AdminOrgSchema>
export const ListOrgsResponseSchema = schemas.apihttp_ListOrgsResponse.describe('ListOrgsResponse')

export async function listOrgs(): Promise<Array<AdminOrg>> {
  const res = await fetch(`${window.location.origin}/api/sysadmin/org`, {
    method: 'GET',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
  const data = parseDataOfType(await res.json(), ListOrgsResponseSchema)
  return data.orgs
}

export async function activateOrgPayment(slug: string): Promise<void> {
  const res = await fetch(`${window.location.origin}/api/sysadmin/org/${encodeURIComponent(slug)}/payment-active`, {
    method: 'PUT',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
}

export const CreateOrgRequestSchema = z
  .object({
    orgName: z.string().min(1, 'Organisation name is required'),
    userEmail: z.email('Please enter a valid email address'),
    userFirstName: z.string().min(2, 'Please provide a valid first name'),
    userSurname: z.string().min(2, 'Please provide a valid surname').optional()
  })
  .describe('CreateOrgRequest')
export type CreateOrgRequest = z.infer<typeof CreateOrgRequestSchema>

export const CreateOrgResponseSchema = z.object({ orgSlug: z.string() }).describe('CreateOrgResponse')

export async function createOrg(req: CreateOrgRequest): Promise<string> {
  const validated = parseDataOfType(req, CreateOrgRequestSchema)
  const res = await fetch(`${window.location.origin}/api/sysadmin/org`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(validated)
  })
  await apiErrorHandler(res)
  const data = parseDataOfType(await res.json(), CreateOrgResponseSchema)
  return data.orgSlug
}

export const InviteOrgUserRequestSchema = z
  .object({
    userEmail: z.email('Please enter a valid email address'),
    userFirstName: z.string().min(2, 'Please provide a valid first name'),
    userSurname: z.string().min(2, 'Please provide a valid surname').optional(),
    isOrgAdmin: z.boolean()
  })
  .describe('InviteOrgUserRequest')
export type InviteOrgUserRequest = z.infer<typeof InviteOrgUserRequestSchema>

export async function inviteUserToOrg(slug: string, req: InviteOrgUserRequest): Promise<void> {
  const validated = parseDataOfType(req, InviteOrgUserRequestSchema)
  const res = await fetch(`${window.location.origin}/api/sysadmin/org/${encodeURIComponent(slug)}/users`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(validated)
  })
  await apiErrorHandler(res)
}

export const BulkInviteUserItemSchema = z.object({
  userEmail: z.email('Please enter a valid email address'),
  userFirstName: z.string().min(2, 'Please provide a valid first name'),
  userSurname: z.string().min(2, 'Please provide a valid surname').optional()
})
export type BulkInviteUserItem = z.infer<typeof BulkInviteUserItemSchema>

export const BulkInviteUsersRequestSchema = z
  .object({ users: z.array(BulkInviteUserItemSchema).min(1).max(100) })
  .describe('BulkInviteUsersRequest')

export const BulkInviteResultSchema = z.object({
  userEmail: z.string(),
  status: z.enum(['invited', 'skipped_existing', 'failed']),
  detail: z.string().optional().default('')
})
export type BulkInviteResult = z.infer<typeof BulkInviteResultSchema>

export const BulkInviteUsersResponseSchema = z
  .object({ results: z.array(BulkInviteResultSchema) })
  .describe('BulkInviteUsersResponse')

export async function bulkInviteUsersToOrg(
  slug: string,
  users: Array<BulkInviteUserItem>
): Promise<Array<BulkInviteResult>> {
  const body = parseDataOfType({ users }, BulkInviteUsersRequestSchema)
  const res = await fetch(`${window.location.origin}/api/sysadmin/org/${encodeURIComponent(slug)}/users/bulk`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  await apiErrorHandler(res)
  const data = parseDataOfType(await res.json(), BulkInviteUsersResponseSchema)
  return data.results
}

export const PromoteSysAdminRequestSchema = z
  .object({ email: z.email('Please enter a valid email address') })
  .describe('PromoteSysAdminRequest')
export type PromoteSysAdminRequest = z.infer<typeof PromoteSysAdminRequestSchema>

export async function promoteUserToSysAdmin(email: string): Promise<void> {
  const body = parseDataOfType({ email }, PromoteSysAdminRequestSchema)
  const res = await fetch(`${window.location.origin}/api/sysadmin/users/promote`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  await apiErrorHandler(res)
}

export const UpdateOrgFeatureRequestSchema = z
  .object({ feature: z.string().min(1), enabled: z.boolean() })
  .describe('UpdateOrgFeatureRequest')

export const OrgFeaturesResponseSchema = z
  .object({
    enabledFeatures: z
      .array(z.string())
      .nullable()
      .transform((v) => v ?? [])
  })
  .describe('OrgFeaturesResponse')

export async function updateOrgFeature(slug: string, feature: string, enabled: boolean): Promise<Array<string>> {
  const body = parseDataOfType({ feature, enabled }, UpdateOrgFeatureRequestSchema)
  const res = await fetch(`${window.location.origin}/api/sysadmin/org/${encodeURIComponent(slug)}/features`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  await apiErrorHandler(res)
  const data = parseDataOfType(await res.json(), OrgFeaturesResponseSchema)
  return data.enabledFeatures
}
